// ============================================
// Configurable Navigation
// Central nav definitions for admin & vendor portals
// ============================================

import {
  LayoutDashboard,
  FileText,
  Database,
  ScrollText,
  ClipboardCheck,
  DollarSign,
  FolderOpen,
  Gamepad2,
  Megaphone,
  Activity,
  Server,
  BarChart3,
  FlaskConical,
  ShieldCheck,
  Handshake,
  Package,
  Download,
  Ruler,
  TrendingUp,
} from 'lucide-react';
import type { AdminRole } from '../services/firestore/adminAuthService';

export interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end: boolean;
  /** Which roles can see this tab. If omitted, only 'owner' can see it. */
  roles: AdminRole[];
  /** Set false to hide without removing from config */
  enabled?: boolean;
}

export interface VendorNavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end: boolean;
  enabled?: boolean;
}

export const ADMIN_NAV_ITEMS: NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, roles: ['owner', 'ceo', 'builder', 'medical_director'] },
  { to: '/admin/promise-keeper', label: 'Promise Keeper', icon: ShieldCheck, end: false, roles: ['owner', 'ceo', 'builder'] },
  { to: '/admin/promises', label: 'Patient Promises', icon: Handshake, end: false, roles: ['owner', 'ceo', 'builder'] },
  { to: '/admin/products', label: 'Formulary', icon: FileText, end: false, roles: ['owner', 'builder', 'medical_director'] },
  { to: '/admin/regulatory', label: 'Regulatory', icon: ScrollText, end: false, roles: ['owner', 'ceo', 'medical_director'] },
  { to: '/admin/data-ops', label: 'Data Ops', icon: ClipboardCheck, end: false, roles: ['owner', 'ceo'] },
  { to: '/admin/financial', label: 'Financial', icon: DollarSign, end: false, roles: ['owner', 'ceo'] },
  { to: '/admin/patient-journey', label: 'Journey Engine', icon: Gamepad2, end: false, roles: ['owner', 'builder'] },
  { to: '/admin/documents', label: 'Documents', icon: FolderOpen, end: false, roles: ['owner'] },
  { to: '/marketing', label: 'Marketing', icon: Megaphone, end: false, roles: ['owner'] },
  { to: '/admin/analytics', label: 'Funnel Analytics', icon: BarChart3, end: false, roles: ['owner', 'ceo'] },
  { to: '/admin/ops', label: 'Platform Health', icon: Activity, end: false, roles: ['owner', 'ceo', 'builder'] },
  { to: '/admin/mcp', label: 'MCP Server', icon: Server, end: false, roles: ['owner'] },
  { to: '/admin/seed', label: 'Seed Data', icon: Database, end: false, roles: ['owner', 'builder'] },
  { to: '/admin/calibration', label: 'Calibration', icon: FlaskConical, end: false, roles: ['owner', 'builder', 'medical_director'] },
  { to: '/admin/unit-mapping', label: 'Unit Mapping', icon: Ruler, end: false, roles: ['owner', 'builder', 'medical_director'] },
  { to: '/admin/model-performance', label: 'Model Performance', icon: TrendingUp, end: false, roles: ['owner', 'builder', 'medical_director'] },
  { to: '/admin/security-audit', label: 'Security Audit', icon: ShieldCheck, end: false, roles: ['owner', 'auditor'] },
];

export const VENDOR_NAV_ITEMS: VendorNavItem[] = [
  { to: '/vendor', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/vendor/orders', label: 'Orders', icon: Package, end: false },
  { to: '/vendor/export', label: 'Export', icon: Download, end: false },
];

/** Filter admin nav by role and enabled status */
export function getVisibleAdminNav(role: AdminRole | null): NavItem[] {
  if (!role) return [];
  return ADMIN_NAV_ITEMS.filter(
    (item) => item.enabled !== false && item.roles.includes(role),
  );
}

/** Filter vendor nav by enabled status */
export function getVisibleVendorNav(): VendorNavItem[] {
  return VENDOR_NAV_ITEMS.filter((item) => item.enabled !== false);
}
